import type { Category } from '@domain/finance'

/**
 * O seletor de categoria, usado na lista e no detalhe da transação.
 *
 * ## Um `<select>` nativo — e o argumento mudou de tamanho (decisão 250)
 *
 * O que estava escrito aqui era: *"São 51 categorias em três níveis. Um seletor
 * próprio precisaria de busca, navegação por teclado, foco preso e leitura de
 * tela — e ainda seria pior no celular"*. Metade disso caiu: esse seletor
 * próprio **existe** (`EscolherCategoria`, sobre a casca da decisão 242), com
 * busca e foco resolvidos, e as 51 viraram a árvore que a casa monta à mão.
 *
 * O que continua valendo é onde ele é usado: **dentro de uma LINHA** da lista
 * de transações, do detalhamento, da nota. Ali a escolha é um detalhe de uma
 * linha entre dezenas, e abrir a tela inteira para cada uma tiraria de vista
 * justamente a lista que se está varrendo. No cadastro de produto, que é um
 * formulário com um campo de família abrindo em tela cheia dois campos acima,
 * o painel é que era o certo — e é o que está lá.
 *
 * A hierarquia entra como indentação no rótulo.
 *
 * ## Categoria arquivada não aparece — mas a que já está escolhida, sim
 *
 * Arquivar é como uma categoria sai dos seletores sem sumir do histórico
 * (decisão da Fase 2). Se a transação já aponta para uma arquivada, esconder a
 * opção faria o `<select>` renderizar **vazio** e a próxima escolha apagaria em
 * silêncio uma classificação que alguém fez de propósito. Ela entra, marcada.
 *
 * ## "Sem categoria" é uma opção, não a ausência de escolha
 *
 * `null` é um valor: descategorizar uma transação classificada por engano tem
 * de ser possível, e é diferente de nunca ter escolhido.
 */

/** O rótulo com a hierarquia visível. `depth` é denormalizado na categoria. */
function label(category: Category): string {
  // Espaço fino repetido: o `<option>` colapsa espaço comum, e sem isso os três
  // níveis viram uma lista plana onde "Feira" e "Mercado" parecem irmãs.
  return `${'  '.repeat(category.depth)}${category.name}`
}

/**
 * Ordena pai antes de filha, e irmãs por nome. `path` é a chave natural: ele é
 * o caminho de slugs, então ordenar por ele já é a ordem da árvore em
 * profundidade — sem uma segunda travessia que possa discordar de
 * `categoryPath`.
 */
function inTreeOrder(categories: Category[]): Category[] {
  return [...categories].sort((a, b) => a.path.join('/').localeCompare(b.path.join('/')))
}

export function CategoryPicker({
  categories,
  value,
  onChange,
  id,
  ariaLabel,
  disabled = false,
}: {
  categories: Category[]
  value: string | null
  onChange: (categoryId: string | null) => void
  id?: string
  ariaLabel?: string
  disabled?: boolean
}) {
  const visiveis = categories.filter((c) => c.archivedAt === null || c.id === value)

  return (
    <select
      id={id}
      aria-label={ariaLabel}
      disabled={disabled}
      value={value ?? ''}
      className="rounded-lg border border-[var(--border)] bg-transparent px-2 py-1 text-sm"
      onChange={(event) => {
        onChange(event.target.value === '' ? null : event.target.value)
      }}
    >
      <option value="">Sem categoria</option>
      {inTreeOrder(visiveis).map((category) => (
        <option key={category.id} value={category.id}>
          {category.archivedAt === null ? label(category) : `${label(category)} (arquivada)`}
        </option>
      ))}
    </select>
  )
}
